import { inject, injectable } from "inversify";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import i18n from "../../../i18n/config";
import type { IUserStore } from "@whatslly/ui-components/services/UserStore";
import { InjectUserStore } from "@whatslly/ui-components/services/UserStore";
import type { IOrganizationStore } from "../../../services/IOrganizationStore";
import { InjectOrganizationStore } from "../../../services/IOrganizationStore";
import { OrgConfigsStore } from "../../../services/OrgConfigsStore";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { getJSON, putJSON } from "@whatslly/commons/apiUtils";
import { hasPermission } from "@whatslly/commons/platformPermissions";
import { UserPermissions } from "@whatslly/commons/granularPermissions";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";
import { parseOrReturnError } from "../../../services/utils";
import { IEliteTokenStore } from "./IEliteTokenStore";

@injectable()
export class EliteTokenStore implements IEliteTokenStore {
  token: string | undefined = undefined;
  loading = false;
  togglingUserAgentHeader = false;

  get canGenerate() {
    return hasPermission(this.userStore.user, UserPermissions["elite_token.update"]);
  }

  get userAgentHeader() {
    return Boolean(this.orgConfigsStore.getConfig("elite_security_token__user_agent_header"));
  }

  constructor(
    @inject(InjectUserStore) private readonly userStore: IUserStore,
    @inject(InjectOrganizationStore) private readonly orgStore: IOrganizationStore,
    @inject(OrgConfigsStore) private readonly orgConfigsStore: OrgConfigsStore,
    @inject(FrontLogger) private readonly logger: FrontLogger
  ) {
    makeObservable(this, {
      token: observable,
      loading: observable,
      canGenerate: computed,
      userAgentHeader: computed,
      togglingUserAgentHeader: observable,
      fetchToken: action.bound,
      saveToken: action.bound,
      toggleUserAgentHeader: action.bound,
    });

    reaction(
      () => this.userStore.user,
      (user) => {
        if (user && this.userStore.etoken) void this.fetchToken();
      },
      { fireImmediately: true }
    );
  }

  private async getOrgId() {
    return this.orgStore.orgId;
  }

  async fetchToken() {
    runInAction(() => {
      this.loading = true;
    });
    try {
      const orgId = await this.getOrgId();
      const res = await getJSON({
        urlSuffix: `/integrations/organizations/elite-token${orgId ? `?orgId=${orgId}` : ""}`,
        etoken: this.userStore.etoken ?? "",
        pod: this.userStore.pod,
      });
      const data = await parseOrReturnError<{ success: boolean; token?: string }>(res, this.logger);
      runInAction(() => {
        this.token = data.success ? data.token : undefined;
      });
    } catch {
      runInAction(() => {
        this.token = undefined;
      });
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  async saveToken(token: string) {
    if (!this.canGenerate) {
      return;
    }
    try {
      const orgId = await this.getOrgId();
      const res = await putJSON({
        urlSuffix: `/integrations/organizations/elite-token${orgId ? `?orgId=${orgId}` : ""}`,
        etoken: this.userStore.etoken ?? "",
        pod: this.userStore.pod,
        body: JSON.stringify({ token }),
      });
      const data = await parseOrReturnError<{ success: boolean; token?: string }>(res, this.logger);
      if (data.success && data.token) {
        runInAction(() => {
          this.token = data.token;
        });
        modalService.openModal(Toast, { text: i18n.t("elite_token_page.save_success") });
      } else {
        modalService.openModal(Toast, { text: i18n.t("elite_token_page.save_error"), type: "error" });
      }
    } catch {
      modalService.openModal(Toast, { text: i18n.t("elite_token_page.save_error"), type: "error" });
    }
  }

  async toggleUserAgentHeader() {
    const orgId = await this.getOrgId();
    if (!orgId) {
      modalService.openModal(Toast, { text: i18n.t("elite_token_page.update_error"), type: "error" });
      return;
    }
    runInAction(() => {
      this.togglingUserAgentHeader = true;
    });
    try {
      await this.orgConfigsStore.saveConfig("elite_security_token__user_agent_header", !this.userAgentHeader);
    } catch {
      modalService.openModal(Toast, { text: i18n.t("elite_token_page.update_error"), type: "error" });
    } finally {
      runInAction(() => {
        this.togglingUserAgentHeader = false;
      });
    }
  }
}
